import axios, { AxiosError } from 'axios';
import { ROOT_URL_V2 } from '../config';
import { TTimesheetApprovalResponse } from '../types/timesheet';
import { TApiResponse } from '../types/api';
import { TDispatch } from '../reducers';

export const FETCH_TIMESHEETS_FOR_APPROVAL = 'FETCH_TIMESHEETS_FOR_APPROVAL';
export const TIMESHEET_APPROVAL_ERROR = 'TIMESHEET_APPROVAL_ERROR';

/**
 * Validate approval token and fetch timesheets
 */
export function fetchTimesheetsForApprovalAsync(
    token: string,
    onSuccess?: (data: TTimesheetApprovalResponse) => void,
    onError?: (error: AxiosError<TApiResponse<unknown>>) => void,
) {
    return async(dispatch: TDispatch) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${ROOT_URL_V2}timesheets/approval/`,
                params: { token },
            });

            // Extract content from nested response structure
            const data: TTimesheetApprovalResponse = response.data.content;

            dispatch({
                type: FETCH_TIMESHEETS_FOR_APPROVAL,
                payload: data,
            });

            if (onSuccess) {
                onSuccess(data);
            }

            return data;
        } catch (error) {
            const axiosError = error as AxiosError<TApiResponse<unknown>>;

            dispatch({
                type: TIMESHEET_APPROVAL_ERROR,
                payload: axiosError.response?.data || { message: 'Failed to fetch timesheets' },
            });

            if (onError) {
                onError(axiosError);
            }

            throw error;
        }
    };
}

/**
 * Approve multiple timesheets
 */
export function approveTimesheetsAsync(
    token: string,
    timesheetIds: number[],
    onSuccess?: () => void,
    onError?: (error: AxiosError<TApiResponse<unknown>>) => void,
) {
    return async() => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${ROOT_URL_V2}timesheets/approval/approve/`,
                data: {
                    token,
                    timesheet_ids: timesheetIds,
                },
            });

            // Extract content from nested response structure if present
            const data = response.data.content;

            if (onSuccess) {
                onSuccess();
            }

            return data;
        } catch (error) {
            if (onError) {
                onError(error as AxiosError<TApiResponse<unknown>>);
            }

            throw error;
        }
    };
}

/**
 * Reject a single timesheet with reason
 */
export function rejectTimesheetAsync(
    token: string,
    timesheetId: number,
    reason: string,
    onSuccess?: () => void,
    onError?: (error: AxiosError<TApiResponse<unknown>>) => void,
) {
    return async() => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${ROOT_URL_V2}timesheets/approval/reject/`,
                data: {
                    token,
                    timesheet_id: timesheetId,
                    reason,
                },
            });

            // Extract content from nested response structure if present
            const data = response.data.content;

            if (onSuccess) {
                onSuccess();
            }

            return data;
        } catch (error) {
            if (onError) {
                onError(error as AxiosError<TApiResponse<unknown>>);
            }

            throw error;
        }
    };
}
